import dayjs from "dayjs";
import * as Yup from "yup";

import { PREFERRED_TIME, TypeMedication } from "@/interfaces/IServiceUser";
import { PREFFERED_TIME } from "@/pages/service/medication/[userId]/add-medication/components/DateInfo";
import { MEDICATION_TYPES } from "@/pages/service/medication/[userId]/add-medication/components/SelectType";
import {
  DAYTIME,
  LIMITS,
  medicationOutcomeRequiresReason,
  REGEX,
} from "./constants";
import { getValues } from "./helpers";

// import { Frequency } from "@/interfaces/ITaskManagement";

const passwordValidation = {
  password: Yup.string()
    .required("Password is Required")
    .min(6, "Password must be at least 6 characters long")
    .matches(
      REGEX.password,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is Required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
};

export const changePasswordValidation = Yup.object().shape({
  newPassword: Yup.string()
    .required("Password is Required")
    .min(6, "Password must be at least 6 characters long")
    .matches(
      REGEX.password,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is Required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

export const profilePasswordValidation = Yup.object().shape({
  oldPassword: Yup.string().required("Old Password is Required"),
  newPassword: Yup.string()
    .required("Password is Required")
    .min(6, "Password must be at least 6 characters long")
    .matches(
      REGEX.password,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is Required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

export const normalPINValidation = Yup.object().shape({
  oldPIN: Yup.string(),
  newPIN: Yup.string()
    .required("PIN is Required")
    .length(4, "PIN must be 4 digits")
    .matches(REGEX.number, "PIN must be a number"),
  confirmPIN: Yup.string()
    .required("Confirm PIN is Required")
    .length(4, "PIN must be 4 digits")
    .oneOf([Yup.ref("newPIN")], "PINs must match")
    .matches(REGEX.number, "PIN must be a number"),
});

export const validateStaffForm = Yup.object().shape({
  firstName: Yup.string()
    .required("First Name is required")
    .max(LIMITS.name, `First name should not exceed ${LIMITS.name} characters`),

  address: Yup.object()
    .required("Address is required")
    .test("has-address-content", "Address is required", function (value) {
      if (!value) return false;
      // Check if at least one address field has content
      const hasContent = Object.values(value).some(
        (val) => val && typeof val === "string" && val.trim() !== ""
      );
      return hasContent;
    })
    .shape({
      buildingName: Yup.string().max(
        LIMITS.name,
        `Building name should not exceed ${LIMITS.text} characters`
      ),
      addressLine1: Yup.string().required("Address Line 1 is required"),
      postCode: Yup.string().required("Postcode is required"),
    }),

  lastName: Yup.string().required("Last Name is required"),

  gender: Yup.string().required("Please select any gender"),
  dateOfBirth: Yup.string().nullable(),
  age: Yup.string(),

  status: Yup.string().required("Please select any status"),
  // jobRoles: Yup.string().required("Please select any job role"),
  jobRoles: Yup.string(),

  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  landlineNumber: Yup.string().matches(REGEX.number, "Invalid landline number"),
  mobileNumber: Yup.string().matches(REGEX.number, "Invalid mobile number"),
  accessLevel: Yup.string().required("Please select any access level"),
  DBSNumber: Yup.string(),
  startDate: Yup.string().required("Start Date is required"),
  contractedHours: Yup.string().matches(
    /^\d{1,3}$/,
    "Contracted hours  cannot exceed  3 digits "
  ),

  contacts: Yup.array().of(
    Yup.object().shape({
      email: Yup.string().email("Invalid email format").nullable(),
      phoneNumber: Yup.string().matches(
        REGEX.number,
        "Invalid contact phone number"
      ),
    })
  ),
  financeDetails: Yup.object().shape({
    accountNumber: Yup.string().matches(
      REGEX.number,
      "Invalid finance details account number"
    ),
  }),
});

export const validateStaffProfileForm = Yup.object()
  .shape({
    firstName: Yup.string()
      .required("First Name is required")
      .max(
        LIMITS.name,
        `First name should not exceed ${LIMITS.name} characters`
      ),

    postCode: Yup.string().nullable(),
    address: Yup.object().when("postCode", {
      is: (postCode: string) => !postCode || postCode.trim() === "",
      then: (schema) =>
        schema.shape({
          buildingName: Yup.string()
            .max(
              LIMITS.name,
              `Building name should not exceed ${LIMITS.text} characters`
            )
            .required("Building Name is required"),
          addressLine1: Yup.string().required("Address Line 1 is required"),
          addressLine2: Yup.string(),
          town: Yup.string().required("Town is required"),
          country: Yup.string().required("Country is required"),
          postCode: Yup.string().required("Postcode is required"),
        }),
      otherwise: (schema) =>
        schema.shape({
          buildingName: Yup.string().max(
            LIMITS.name,
            `Building name should not exceed ${LIMITS.text} characters`
          ),
          addressLine1: Yup.string(),
          addressLine2: Yup.string(),
          town: Yup.string(),
          country: Yup.string(),
          postCode: Yup.string(),
        }),
    }),

    lastName: Yup.string().required("Last Name is required"),

    gender: Yup.string().required("Please select any gender"),
    dateOfBirth: Yup.string().nullable(),
    age: Yup.string(),

    status: Yup.string().required("Please select any status"),
    jobRoles: Yup.string(),

    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    landlineNumber: Yup.string().matches(
      REGEX.number,
      "Invalid landline number"
    ),
    mobileNumber: Yup.string().matches(REGEX.number, "Invalid mobile number"),
    accessLevel: Yup.string(),
    DBSNumber: Yup.string(),
    startDate: Yup.string().required("Start Date is required"),

    contractedHours: Yup.string().matches(
      /^\d{1,3}$/,
      "Contracted hours  cannot exceed  3 digits "
    ),

    contacts: Yup.array().of(
      Yup.object().shape({
        email: Yup.string().email("Invalid email format").nullable(),
        phoneNumber: Yup.string().matches(
          REGEX.number,
          "Invalid contact phone number"
        ),
      })
    ),
    financeDetails: Yup.object().shape({
      accountNumber: Yup.string().matches(
        REGEX.number,
        "Invalid finance details account number"
      ),
    }),
  })
  .test("address-required", "Address is required", function (values) {
    const { postCode, address } = values;

    const isPostCodeFilled = Boolean(postCode?.trim());
    const isAddressFilled = Boolean(
      address &&
      Object.values(address).some(
        (val) => typeof val === "string" && val?.trim()
      )
    );

    if (!isPostCodeFilled && !isAddressFilled) {
      return this.createError({
        path: "postCode",
        message:
          "Address is required (either use search or fill manual address)",
      });
    }

    return true;
  });

export const validateLoginForm = Yup.object().shape({
  email: Yup.string().email("Invalid Email").required("Email is required"),
  password: Yup.string().required("Password is Required"),
});

export const validateSignUpForm = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  companyName: Yup.string().required("Company Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .matches(REGEX.email, "Invalid email format")
    .required("Email is required"),
  serviceType: Yup.string().required("Service Type is required"),
  jobTitle: Yup.string().required("Job Title is required"),
  phone: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .max(13, "Phone number is too long")
    .required("Phone is required"),
  activeServiceUsers: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .required("Active Service Users is required"),
  noOfDifferentSites: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .required("Number of Different Sites is required"),
  termsAndConditions: Yup.boolean().required(
    "Please accept terms and conditions"
  ),
  // ...passwordValidation,
});

export const validateProfile = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  companyName: Yup.string().required("Company Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .matches(REGEX.email, "Invalid email format")
    .required("Email is required"),
  serviceType: Yup.string().required("Service Type is required"),
  jobTitle: Yup.string().required("Job Title is required"),
  phone: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .required("Phone is required"),
  activeServiceUsers: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .required("Active Service Users is required"),
  noOfDifferentSites: Yup.string()
    .matches(REGEX.number, "Invalid number")
    .required("Number of Different Sites is required"),
});

export const validateForgotPasswordForm = Yup.object().shape({
  email: Yup.string().email("Invalid Email").required("Email is required"),
});
export const validateResetPasswordForm = Yup.object().shape({
  ...passwordValidation,
});

const parseTimeStringToDate = (timeString: string) => {
  const [hours, minutes] = timeString.split(":");
  const date = new Date();
  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));
  return date;
};

export const shiftPasscodeValidation = Yup.object({
  shiftType: Yup.string().required("Required"),
  startDate: Yup.string().required("Required"),
  endDate: Yup.string().required("Required"),
  startTime: Yup.string()
    .required("Start time is required")
    .test(
      "is-later",
      "Start time must be earlier than end time",
      function (value) {
        const { endTime } = this.parent;
        if (!value || !endTime) return true; // Allow empty values
        return parseTimeStringToDate(value) < parseTimeStringToDate(endTime);
      }
    ),
  endTime: Yup.string()
    .required("End time is required")
    .test(
      "is-earlier",
      "End time must be later than start time",
      function (value) {
        const { startTime } = this.parent;
        if (!value || !startTime) return true; // Allow empty values
        return parseTimeStringToDate(value) > parseTimeStringToDate(startTime);
      }
    ),
  duration: Yup.number().min(1, "Duration cannot be zero").required("Required"),
  shiftPassword: Yup.string().required("Required"),
  confirmShiftPassword: Yup.string()
    .required("Required")
    .oneOf([Yup.ref("shiftPassword")], "Passwords must match"),
});

export const documentsValidation = (
  options: {
    hideCategory?: boolean;
    hideReviewDate?: boolean;
  } = {}
) => {
  const { hideCategory = false, hideReviewDate = false } = options;

  return Yup.object({
    docTitle: Yup.string().required("Required"),

    category: hideCategory
      ? Yup.string().nullable()
      : Yup.string().required("Required"),

    reviewDate: hideReviewDate
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .nullable()
          .test("is-null-or-valid-date", "Invalid date", (value: any) => {
            return !value || dayjs(value).isValid();
          }),

    document: Yup.mixed().required("Required"),
  });
};

export const supportDocumentValidation = Yup.object().shape({
  docTitle: Yup.string().required("Required"),
  document: Yup.mixed().required("Required"),
});
export const calenderValidation = Yup.object()
  .shape({
    title: Yup.string()
      .required("Title is required")
      .max(LIMITS.text, `Title cannot exceed ${LIMITS.text} characters`),
    time: Yup.string()
      .required("Time is required")
      .test("is-valid-time", "Please enter a valid time", (value) => {
        if (!value) return false;
        return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
      }),
    // date: Yup.date().required("Date is required").nullable(),

    date: Yup.date()
      .required("Date is required")
      .typeError("Please select a valid date"),

    description: Yup.string().required("Description is required"),
    address: Yup.object().shape({
      buildingName: Yup.string(),
      addressLine1: Yup.string(),
      addressLine2: Yup.string(),
      townCity: Yup.string(),
      country: Yup.string(),
      personal: Yup.string(),
    }),
    googleAddress: Yup.string(),
  })
  .test("oneOfFields", "Address is required", function (values) {
    const { address, googleAddress } = values;
    const addressCheck = Object.values(address).some((value) => !!value);

    if (!addressCheck && !googleAddress) {
      this.createError({
        path: "address.buildingName",
        message: "Address is required",
      });
      return this.createError({
        path: "googleAddress",
        message: "Address is required",
      });
    }
    return true;
  });
export const validateServiceUserForm = Yup.object()
  .shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    gender: Yup.string().required("Please select any gender"),
    dateOfBirth: Yup.string(),
    // age: Yup.string(),
    age: Yup.string(),

    status: Yup.string(),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),

    landlineNumber: Yup.string().matches(
      REGEX.number,
      "Invalid landline number"
    ),
    telephoneNumber: Yup.string().matches(
      REGEX.number,
      "Invalid mobile number"
    ),
    joiningDate: Yup.string().required("Joining Date is required"),
    fundingSource: Yup.string().required("Please select any funding source"),
    contacts: Yup.array().of(
      Yup.object().shape({
        email: Yup.string().email("Invalid email format").nullable(),
        phoneNumber: Yup.string().matches(
          REGEX.number,
          "Invalid contact phone number"
        ),
      })
    ),

    postCode: Yup.string().nullable(),
    address: Yup.object().when("postCode", {
      is: (postCode: string) => !postCode || postCode.trim() === "",
      then: (schema) =>
        schema.shape({
          buildingName: Yup.string().required("Building Name is required"),
          addressLine1: Yup.string().required("Address Line 1 is required"),
          addressLine2: Yup.string(),
          town: Yup.string().required("Town is required"),
          country: Yup.string().required("Country is required"),
          postCode: Yup.string().required("Postcode is required"),
        }),
      otherwise: (schema) =>
        schema.shape({
          buildingName: Yup.string(),
          addressLine1: Yup.string(),
          addressLine2: Yup.string(),
          town: Yup.string(),
          country: Yup.string(),
          postCode: Yup.string(),
        }),
    }),

    isGPSCheckInActive: Yup.boolean(),
    addressCoordinates: Yup.object().when("isGPSCheckInActive", {
      is: true,
      then: (schema) =>
        schema
          .shape({
            lat: Yup.number().required(
              "Valid address is required when GPS Check-In is active"
            ),
            lng: Yup.number().required(
              "Valid address is required when GPS Check-In is active"
            ),
          })
          .required(
            "Address coordinates are required when GPS Check-In is active"
          ),
      otherwise: (schema) => schema,
    }),
    checkInRadius: Yup.number().when("isGPSCheckInActive", {
      is: true,
      then: (schema) =>
        schema.required(
          "A check-in radius is required when GPS check-in is active."
        ),
      otherwise: (schema) => schema,
    }),
  })
  .test("address-required", "Address is required", function (values) {
    const { postCode, address } = values;

    const isPostCodeFilled = Boolean(postCode?.trim());
    const isAddressFilled = Boolean(
      address &&
      Object.values(address).some(
        (val) => typeof val === "string" && val?.trim()
      )
    );

    if (!isPostCodeFilled && !isAddressFilled) {
      return this.createError({
        path: "postCode",
        message:
          "Address is required (either use search or fill manual address)",
      });
    }

    return true;
  })
  .test(
    "oneOfFields",
    "Please fill either Date of Birth or Age",
    function (values) {
      const { dateOfBirth, age } = values;
      if (!dateOfBirth && !age) {
        return this.createError({
          path: "dateOfBirth",
          message: "Please fill either Date of Birth or Age",
        });
      }
      return true;
    }
  );

export const zoneValidation = Yup.object({
  title: Yup.string().required("Required"),
  serviceUsers: Yup.array()
    .of(Yup.mixed())
    .min(1, "Please add atleast one service user")
    .required(),
});

export const mileageGroupValidation = Yup.object({
  title: Yup.string().required("Required"),
  staff: Yup.array()
    .of(Yup.mixed())
    .min(1, "Please add at least one staff member")
    .required(),
});

export const trainingRecordValidation = Yup.object({
  trainingCourse: Yup.string().required("Training course name is required"),

  validTill: Yup.string().required("Valid till date is required"),
  attendees: Yup.array()
    .of(Yup.mixed())
    .min(1, "Please add atleast one attendee")
    .required(),
  document: Yup.mixed(),
  status: Yup.string().required("Status is required"),
});

export const bodyNoteCommentValidation = Yup.object({
  description: Yup.string().required("Description is required"),
  injuryCondition: Yup.string().required("Injury condition is required"),
});

export const incompatibilityValidationSchema = Yup.object().shape({
  staff: Yup.string().required("Staff is required"),
  reason: Yup.string().required("Reason is required"),
});

export const taskManagementValidation = Yup.object().shape({
  selectedUsers: Yup.array()
    .min(1, "Please select at least one user")
    .required("Please select at least one user"),
  selectedCategory: Yup.object()
    .shape({
      label: Yup.string().required("Category required"),
      value: Yup.string().required("Category required"),
    })
    .required("Category required")
    .typeError("Category required"),
  selectedTasks: Yup.array()
    .min(1, "Please select at least one task")
    .required("Please select at least one task"),
  startDate: Yup.date().required("Start date is required"),
  description: Yup.string().required(" description is required"),
  idealTime: Yup.string()
    .oneOf(["anytime", "session", "specific-time"])
    .required("Ideal time required"),
  dayTime: Yup.array()
    .of(Yup.string())
    .when("idealTime", {
      is: "session",
      then: (schema) =>
        schema
          .required("Please select period of the day")
          .min(1, "Please select atleast 1 period of the day"),
      otherwise: (schema) => schema,
    }),
  startTime: Yup.string().when("idealTime", {
    is: "specific-time",
    then: (schema) => schema.required("Start time is required"),
    otherwise: (schema) => schema,
  }),
  recurring: Yup.boolean(),
  rrule: Yup.object().when("recurring", {
    is: true,
    then: (schema) =>
      schema.shape({
        frequency: Yup.string()
          .required("Please select frequency")
          .oneOf(["daily", "weekly", "biweekly", "monthly", "custom"]),

        interval: Yup.string().when("frequency", {
          is: "custom",
          then: (schema) =>
            schema
              .oneOf(["day", "week"])
              .required("Repeat interval is required when frequency is custom"),
        }),
        intervalCount: Yup.number().when("frequency", {
          is: "custom",
          then: (schema) =>
            schema.required(
              "Repeat count is required when frequency is custom"
            ),
        }),
      }),
    otherwise: (schema) => schema,
  }),
  noteSubmitRequired: Yup.boolean(),
});

const requiredOption = (message: string) =>
  Yup.object()
    .shape({ value: Yup.string().required(message) })
    .nullable()
    .required(message);

export const taskTemplateValidation = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("Template title is required")
    .min(3, "Title must be at least 3 characters")
    .max(LIMITS.text, `Title must be ${LIMITS.text} characters or fewer`),
  category: requiredOption("Select a category"),
  task: requiredOption("Select a task"),
  description: Yup.string(),
});

export const ScheduleValidationSchema = Yup.object({
  startTime: Yup.string()
    .required("Start time is required")
    .test(
      "is-not-equal-endTime",
      "Start time and end time cannot be the same",
      function (value) {
        const { endTime } = this.parent;
        if (value && endTime) {
          return (
            dayjs(value).format("HH:mm") !== dayjs(endTime).format("HH:mm")
          );
        }
        return true;
      }
    ),
  endTime: Yup.string().required("End time is required"),
  // (Removed commented-out validation code for startDate and endDate)

  startDate: Yup.string()
    .required("Start date is required")
    .test("not-in-past", "Start date cannot be in the past", function (value) {
      if (!value) return true;
      const selectedDate = new Date(value);
      const today = new Date();

      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      return selectedDate >= today;
    }),

  endDate: Yup.string().required("End date is required"),

  carerReq: Yup.number()
    .required("Carer is required")
    .integer("Carer must be a whole number")
    .min(1, "At least one Carer is required"),
});

export const medicationListValidationSchema = Yup.object({
  pharmacyName: Yup.string().required("Pharmacy name is required"),
  pharmacyAddress: Yup.string().required("Pharmacy address is required"),
  pharmacyPostcode: Yup.string().required("Pharmacy postcode is required"),
});

export const medicationListSchema = Yup.object().shape({
  name: Yup.string().optional(),
  medicalSupport: Yup.boolean().optional(),

  googleAddress: Yup.string(),

  pharmacyName: Yup.string().max(
    LIMITS.text,
    `Pharmacy Name  should not exceed ${LIMITS.text} characters`
  ),

  pharmacyAddress: Yup.object()
    .shape({
      shortAddress: Yup.string()
        .max(
          LIMITS.text,
          `Short Address should not exceed ${LIMITS.text} characters`
        )
        .optional(),
      buildingName: Yup.string()
        .max(
          LIMITS.text,
          `Building Name should not exceed ${LIMITS.text} characters`
        )
        .optional(),
      addressLine1: Yup.string()
        .max(
          LIMITS.text,
          `Address Line 1 should not exceed ${LIMITS.text} characters`
        )
        .optional(),
      addressLine2: Yup.string()
        .max(
          LIMITS.text,
          `Address Line 2 should not exceed ${LIMITS.text} characters`
        )
        .optional(),
      town: Yup.string()
        .max(
          LIMITS.text,
          `Town/City should not exceed ${LIMITS.text} characters`
        )
        .optional(),
      country: Yup.string()
        .max(LIMITS.text, `Country should not exceed ${LIMITS.text} characters`)
        .optional(),
    })
    .optional(), // Mark the entire pharmacyAddress as optional

  pharmacyPostcode: Yup.string().max(
    LIMITS.postCode,
    `Post Code should not exceed ${LIMITS.postCode} digit`
  ),
});

export const editMedicationValidation = Yup.object().shape({
  startDate: Yup.date().required("Start Date is required"),
  endDate: Yup.date().required("End Date is required"),
  reason: Yup.string().required("Reason is required"),
  preferredTiming: Yup.string().required("Preferred timing is required"),
  preferredTime: Yup.string().required(" Time is required"),
});

export const medicationValidationSchema = Yup.object({
  outcome: Yup.string().required("Outcome is required"),
  reason: Yup.string().when("outcome", {
    is: medicationOutcomeRequiresReason,

    then: (schema) => schema.required("Reason is required"),
    otherwise: (schema) => schema,
  }),
  note: Yup.string(),
});

export const allocateAnotherStaffSchema = Yup.object({
  staff: Yup.object({
    label: Yup.string().required("Required!"),
    value: Yup.string().required("Required!"),
  }),
  type: Yup.string().required("Type is required"),
});

export const inActivityValidationSchema = Yup.object({
  type: Yup.string()
    .oneOf(["", "temporary", "permanent"])
    .required("Required!"),
  reason: Yup.string().required("Reason is required"),
  additionalDetails: Yup.string()
    .required("Additional details are required")
    .max(
      LIMITS.description,
      `Additional details must be ${LIMITS.description} characters or less`
    ),
  start: Yup.mixed().required("Start date is required"),
  end: Yup.mixed().nullable(),
});

const medicationTypeOptions = getValues(MEDICATION_TYPES);
const preferredTimeOptions = getValues(PREFFERED_TIME);
const dayTimeOptions = getValues(DAYTIME);
const supportOptionOptions = ["administer", "assist", "prompt"];

export const medicationValidation = Yup.object().shape({
  medicine: Yup.string()
    .max(
      LIMITS.text,
      `Medicine name should not exceed ${LIMITS.text} characters`
    )
    .required("Medicine name is required"),
  type: Yup.string()
    .required("Select medication type")
    .oneOf(medicationTypeOptions),
  dose: Yup.string().required("Dose is required"),
  route: Yup.string().required("Route is required"),
  startDate: Yup.date().required("Start Date is required"),
  endDate: Yup.date()
    .nullable()
    .test("date-check", "End date must be after start date", function (value) {
      if (!value) return true;
      const { startDate } = this.parent;

      return dayjs(value).isAfter(dayjs(startDate));
    }),
  howManyTimesADay: Yup.number().when("type", {
    is: (type: TypeMedication) =>
      [TypeMedication.SCHEDULED, TypeMedication.BLISTER_PACK].includes(type),
    then: (schema) => schema.required("Please fill how many times in a day"),
    otherwise: (schema) => schema,
  }),
  // instructions: Yup.string(),
  instructions: Yup.string().max(
    LIMITS.description,
    `Instructions should not exceed ${LIMITS.description} characters`
  ),
  preferredTime: Yup.string().when("type", {
    is: (type: TypeMedication) =>
      [TypeMedication.SCHEDULED, TypeMedication.BLISTER_PACK].includes(type),
    then: (schema) =>
      schema.required("Please fill preferred time").oneOf(preferredTimeOptions),
    otherwise: (schema) => schema,
  }),
  timePeriod: Yup.array()
    .of(
      Yup.string().oneOf(
        dayTimeOptions,
        ({ value }) =>
          `${value} is not a valid value for time period, it must be one of (${dayTimeOptions.join(", ")})`
      )
    )
    .when("preferredTime", {
      is: PREFERRED_TIME.TIME_PERIOD,
      then: (schema) =>
        schema
          .required(
            "Time period is required when preferred time is set to time period"
          )
          .test(
            "length-check",
            "Number of time periods must match number of times a day",
            function (value) {
              const { howManyTimesADay } = this.parent;

              if (howManyTimesADay > 4) {
                return value.length >= 4;
              } else {
                return value.length === howManyTimesADay;
              }
              // Custom validation logic
              // 'value' is the value of the field being validated
              // 'this' provides context and access to other fields
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  specificTime: Yup.array()
    .of(Yup.date().typeError("Each time must be a valid time"))
    .when("preferredTime", {
      is: PREFERRED_TIME.SPECIFIC_TIME,
      then: (schema) =>
        schema
          .required(
            "Specific times are required when preferred time is set to specific time"
          )
          .min(
            Yup.ref("howManyTimesADay"),
            "Please fill all the specific times"
          )
          .max(
            Yup.ref("howManyTimesADay"),
            "Please fill all the specific times"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  supportOption: Yup.string()
    .required("Please select an option")
    .oneOf(
      ["administer", "assist", "prompt"],
      "Please select a valid support option"
    ),
  cqcGuidelineLink: Yup.string().optional(),

  rrule: Yup.object().when("type", {
    is: (type: TypeMedication) =>
      [TypeMedication.SCHEDULED, TypeMedication.BLISTER_PACK].includes(type),
    then: (schema) =>
      schema.shape({
        frequency: Yup.string().required("Frequency is required"),
        byweekday: Yup.array()
          .of(Yup.number())
          .when(["frequency", "interval"], {
            is: (frequency: string, interval: string) =>
              frequency === "custom" && interval === "in-week",
            then: (schema) => schema.required("Please select at least one day"),
            otherwise: (schema) => schema,
          }),
        byhour: Yup.array().of(
          Yup.number()
            .min(0, "Hour must be between 0 and 23")
            .max(23, "Hour must be between 0 and 23")
            .typeError("Hour must be a valid number")
        ),
        byminute: Yup.array().of(
          Yup.number()
            .min(0, "Minute must be between 0 and 59")
            .max(59, "Minute must be between 0 and 59")
            .typeError("Minute must be a valid number")
        ),

        interval: Yup.string().when("frequency", {
          is: "custom",
          then: (schema) =>
            schema
              .oneOf(["day", "week", "in-week"])
              .required("Repeat interval is required when frequency is custom"),
        }),
        intervalCount: Yup.number()
          .positive("Repeat count must be a positive number")
          .integer("Repeat count must be an integer")
          .when("frequency", {
            is: "custom",
            then: (schema) =>
              schema.required(
                "Repeat count is required when frequency is custom"
              ),
          }),
      }),
    otherwise: (schema) => schema,
  }),
});
